import type { ReactiveController, ReactiveControllerHost } from 'lit';
import { container } from 'tsyringe';

import { MessageBroker } from '../../message-broker/message-broker';
import type { BaseMessage } from '../../message-broker/base-message.interface';
import type { EntityCreatedPayload } from '../../message-broker/payloads/entity-created.payload';
import type { EntityChangedPayload } from '../../message-broker/payloads/entity-changed.payload';
import type { EntityDeletedPayload } from '../../message-broker/payloads/entity-deleted.payload';
import type { Entity } from '../../domain/entities/entity';
import type { ClassOf } from '../../types/class-of.type';
import type { RxjsLifecycleController } from './rxjs-lifecycle.controller';

/**
 * Host shape required by EntityLiveRefreshController: a Lit ReactiveControllerHost
 * that also exposes a RxjsLifecycleController for reconnect-safe auto-cleanup.
 */
export type EntityLiveRefreshHost = ReactiveControllerHost & {
    rxjs: RxjsLifecycleController;
};

/**
 * Configuration options for EntityLiveRefreshController.
 */
export interface EntityLiveRefreshOptions {
    /**
     * Lazy accessor for the configured entity class. Returning null disables
     * all listener registration (the host has no entity binding).
     */
    getEntityClass: () => ClassOf<Entity> | null;

    /**
     * Callback invoked after each matching entity.created / entity.changed /
     * entity.deleted event. Errors are logged via console.error (no silent failure).
     */
    onReload: () => Promise<void>;
}

/**
 * Reactive Controller that subscribes to entity.created, entity.changed and
 * entity.deleted MessageBroker events and triggers an onReload callback whenever
 * an event matches the host's configured entity class via strict referential
 * equality (===).
 *
 * Listeners are registered with `disconnect: host.rxjs.disconnected$` so they
 * are auto-cleaned by the host's RxjsLifecycleController on DOM removal.
 *
 * @see PRD-061 — Combobox Entity Live-Refresh
 */
export class EntityLiveRefreshController implements ReactiveController {
    constructor(
        private readonly host: EntityLiveRefreshHost,
        private readonly options: EntityLiveRefreshOptions,
    ) {
        host.addController(this);
    }

    /**
     * Registers MessageBroker listeners for the configured entity class. Skips
     * registration when getEntityClass() currently returns null (host without
     * an entity binding has nothing to refresh).
     */
    public hostConnected(): void {
        if (this.options.getEntityClass() === null) {
            return;
        }

        const broker = container.resolve(MessageBroker);
        const disconnect = this.host.rxjs.disconnected$;
        const matchesPrototype = (msg: BaseMessage<EntityCreatedPayload<Entity> | EntityChangedPayload<Entity>>) =>
            msg.payload.prototype === this.options.getEntityClass();

        broker.registerListener<EntityCreatedPayload<Entity>>(this, {
            messageType: 'entity.created',
            filterFn: matchesPrototype,
            method: () => this._invokeReload(),
            disconnect,
        });

        broker.registerListener<EntityChangedPayload<Entity>>(this, {
            messageType: 'entity.changed',
            filterFn: matchesPrototype,
            method: () => this._invokeReload(),
            disconnect,
        });

        broker.registerListener<EntityDeletedPayload<Entity>>(this, {
            messageType: 'entity.deleted',
            filterFn: (msg: BaseMessage<EntityDeletedPayload<Entity>>) =>
                msg.payload.entity.constructor === this.options.getEntityClass(),
            method: () => this._invokeReload(),
            disconnect,
        });
    }

    /**
     * Invokes the onReload callback and forwards any rejection to console.error
     * so the failure is visible (no silent swallow).
     */
    private _invokeReload(): void {
        this.options.onReload().catch((error: unknown) => {
            console.error('[EntityLiveRefreshController] onReload failed', error);
        });
    }
}
